import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { forwardRef, useState, useRef, useCallback } from "react";
import assert from "assert";
import { Box } from "../Box/Box.mjs";
import { formatRanges } from "./formatRanges.mjs";
import { Field } from "../private/Field/Field.mjs";
import { getCharacterLimitStatus } from "../private/Field/getCharacterLimitStatus.mjs";
import { TextareaAction } from "./Actions/TextareaAction.mjs";
import { highlights, field, noResize, textAreaWithLabel, contextButtons } from "./Textarea.css.mjs";
const pxToInt = (str) => typeof str === "string" ? parseInt(str.replace("px", ""), 10) : 0;
const calculateLines = (target, lines, lineLimit) => {
  const { paddingBottom, paddingTop, lineHeight } = window.getComputedStyle(target);
  if (!lineHeight.endsWith("px")) {
    return lines;
  }
  const padding = pxToInt(paddingTop) + pxToInt(paddingBottom);
  const currentRows = Math.floor((target.scrollHeight - padding) / pxToInt(lineHeight));
  if (target && target.value === "") {
    return lines;
  }
  return typeof lineLimit === "number" && currentRows > lineLimit ? lineLimit : currentRows;
};
const Textarea = forwardRef(
  ({
    value,
    onChange,
    onKeyDown,
    onBeforeInput,
    onBlur,
    onFocus,
    onPaste,
    placeholder,
    characterLimit,
    highlightRanges: highlightRangesProp = [],
    lines = 3,
    lineLimit,
    grow = true,
    resize = true,
    tone,
    spellCheck,
    actions = [],
    ...restProps
  }, ref) => {
    assert((actions == null ? void 0 : actions.length) <= 3, "You may pass a maximum of 3 actions.");
    actions.forEach((action) => {
      if (!Object.values(TextareaAction).includes(action.type)) {
        throw new Error(`Actions can only contain TextareaActions.`);
      }
    });
    const [rows, setRows] = useState(lines);
    const highlightsRef = useRef(null);
    const updateScroll = useCallback(
      (scrollTop) => {
        if (highlightsRef.current) {
          highlightsRef.current.scrollTop = scrollTop;
        }
      },
      [highlightsRef]
    );
    const inputLength = String(value).length;
    const hasExceededCharacterLimit = characterLimit && inputLength > characterLimit;
    const highlightTone = !hasExceededCharacterLimit && (tone === "critical" || tone === "caution" || tone === "neutral") ? tone : "critical";
    const highlightRanges = hasExceededCharacterLimit ? [{ start: characterLimit }] : highlightRangesProp;
    const hasHighlights = highlightRanges.length > 0;
    const hasLabel = "label" in restProps;
    return /* @__PURE__ */ jsx(
      Field,
      {
        ...restProps,
        tone,
        value,
        icon: void 0,
        prefix: void 0,
        labelId: void 0,
        secondaryMessage: characterLimit ? getCharacterLimitStatus({
          value,
          characterLimit
        }) : null,
        children: (overlays, { className, borderRadius, background, ...fieldProps }) => /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs(Box, { width: "full", background, borderRadius, children: [
            /* @__PURE__ */ jsxs(Box, { position: "relative", zIndex: 0, children: [
              hasHighlights ? /* @__PURE__ */ jsx(
                Box,
                {
                  ref: highlightsRef,
                  position: "absolute",
                  overflow: "hidden",
                  pointerEvents: "none",
                  height: "full",
                  "aria-hidden": "true",
                  top: 0,
                  left: 0,
                  className: [highlights, className],
                  style: hasLabel ? { paddingTop: 0 } : {},
                  ...fieldProps,
                  children: formatRanges(String(value), highlightRanges, highlightTone)
                }
              ) : null,
              /* @__PURE__ */ jsx(
                Box,
                {
                  component: "textarea",
                  position: "relative",
                  zIndex: 1,
                  rows,
                  value,
                  onChange: (e) => {
                    if (grow) {
                      setRows(calculateLines(e.currentTarget, lines, lineLimit));
                    }
                    if (typeof onChange === "function") {
                      onChange(e);
                    }
                    if (hasHighlights) {
                      updateScroll(e.currentTarget.scrollTop);
                    }
                  },
                  onBlur,
                  onKeyDown,
                  onBeforeInput,
                  onFocus,
                  onPaste,
                  onScroll: hasHighlights ? (event) => updateScroll(event.currentTarget.scrollTop) : void 0,
                  placeholder: !restProps.disabled && !hasLabel ? placeholder : void 0,
                  spellCheck,
                  className: [
                    field,
                    className,
                    !resize ? noResize : void 0,
                    hasLabel && textAreaWithLabel
                  ],
                  ...fieldProps,
                  ref
                }
              )
            ] }),
            actions.length > 0 && /* @__PURE__ */ jsx(Box, { padding: "small", overflow: "auto", children: /* @__PURE__ */ jsx(Box, { display: "flex", gap: "large", className: contextButtons, children: actions == null ? void 0 : actions.map((action, index) => /* @__PURE__ */ jsx(Box, { children: action }, index)) }) })
          ] }),
          overlays
        ] })
      }
    );
  }
);
Textarea.displayName = "Textarea";
export {
  Textarea
};
